/**
 * MCP `ui_open_tab('storage-explorer', …)` with Data Lake refs (S8 Task 8.12): a file opens its parent directory, and a
 * resource-scoped attachment refuses a location outside its scope — the Blob and Data Lake spellings of one directory
 * are the same place (Task 8.3's equivalence), so `lake/raw/` (Blob prefix) is inside an `hnsPath` scope `lake/raw`.
 */
import { storageResourceKey, type HnsPathRef, type StorageResourceRef } from '../../../../types/azureStorage';
import { hnsDirectoryOfBlobRoot, mapLocationIntoRoot } from './hnsView';

/** A Data Lake file → its parent directory (a tab location is a directory). */
export function hnsLocationForTab(ref: HnsPathRef): HnsPathRef {
  if (ref.isDirectory) return ref;
  const slash = ref.path.lastIndexOf('/');
  return { kind: 'hnsPath', fileSystem: ref.fileSystem, path: slash < 0 ? '' : ref.path.slice(0, slash), isDirectory: true };
}

const canonical = (ref: StorageResourceRef): string => storageResourceKey(ref.kind === 'container' || ref.kind === 'blobPrefix' ? hnsDirectoryOfBlobRoot(ref) ?? ref : ref);

/** True when `location` (container / prefix / Data Lake directory) lies outside the attachment's `scope`. */
export function isOutsideAttachmentScope(scope: StorageResourceRef, location: StorageResourceRef): boolean {
  if (scope.kind !== 'container' && scope.kind !== 'blobPrefix' && scope.kind !== 'hnsPath') return false;
  if (location.kind !== 'container' && location.kind !== 'blobPrefix' && location.kind !== 'hnsPath') return false;
  const mapped = mapLocationIntoRoot(scope, location);
  if (canonical(mapped) !== canonical(scope)) return false;
  return canonical(location) !== canonical(scope);
}
